import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Patient } from '../types';
import { api } from '../services/api';
import { Search, Plus, User } from 'lucide-react';

interface PatientInputProps {
  selectedPatient: string;
  onPatientChange: (patientId: string) => void;
  onShowAddModal: () => void;
  refreshTrigger?: number;
}

export const PatientInput: React.FC<PatientInputProps> = ({
  selectedPatient,
  onPatientChange,
  onShowAddModal,
  refreshTrigger = 0,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load patients on mount and when refreshTrigger changes
  useEffect(() => {
    loadPatients();
  }, [refreshTrigger]);

  // Calculate and update dropdown position
  const updateDropdownPosition = () => {
    if (inputContainerRef.current) {
      const rect = inputContainerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  // Update position when dropdown opens and on scroll/resize
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(target) &&
        inputContainerRef.current &&
        !inputContainerRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const loadPatients = async () => {
    try {
      setIsLoading(true);
      const patientsData = await api.getPatients();
      setPatients(patientsData);
    } catch (error) {
      console.error('Error loading patients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePatientSelect = (patientId: string) => {
    onPatientChange(patientId);
    setIsOpen(false);
  };

  // Helper function to get patient name from ID
  const getPatientNameById = (patientId: string): string => {
    const patient = patients.find(p => p.id === patientId);
    return patient ? patient.name : patientId;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
  };

  // Render dropdown content
  const renderDropdown = () => {
    if (!isOpen) return null;

    return (
      <div 
        ref={dropdownRef}
        className="absolute z-[9999]"
        style={{ 
          top: `${dropdownPosition.top + 8}px`, // 8px gap below input
          left: `${dropdownPosition.left}px`, 
          width: `${dropdownPosition.width}px` 
        }}
      >
        <div className="card bg-base-100 shadow-xl border border-base-300">
          <div className="card-body p-0">
            {isLoading ? (
              <div className="p-4 text-center text-base-content/70">
                <span className="loading loading-spinner loading-sm mr-2"></span>
                Loading patients...
              </div>
            ) : (
              <>
                <div
                  className="p-4 cursor-pointer hover:bg-base-200 transition-colors border-b border-base-300 text-primary-content font-medium flex items-center gap-2"
                  onClick={() => {
                    setIsOpen(false);
                    onShowAddModal();
                  }}
                >
                  <Plus size={16} />
                  <span>Add Patient</span>
                </div>
                
                {patients.length > 0 ? (
                  <div className="max-h-60 overflow-y-auto">
                    {patients.map((patient) => (
                      <div
                        key={patient.id}
                        className="p-4 cursor-pointer hover:bg-base-200 transition-colors border-b border-base-300 last:border-b-0"
                        onClick={() => handlePatientSelect(patient.id)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-primary/10 text-primary-content rounded-lg flex items-center justify-center">
                            <User size={16} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-base-content">{patient.name}</div>
                            <div className="text-sm text-base-content/70">
                              Last seen: {formatDate(patient.lastSeen)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-base-content/70">
                    No patients found
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="relative w-full" ref={inputContainerRef}>
        <div className="join w-full">
          <div className="join-item flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 z-10" />
            <input
              type="text"
              placeholder="Add or select patient"
              value={selectedPatient ? getPatientNameById(selectedPatient) : ''}
              onChange={() => {
                // For now, don't allow direct editing - users should select from dropdown
              }}
              onFocus={() => setIsOpen(true)}
              readOnly
              className="input input-bordered w-full pl-10 pr-10 text-base-content bg-base-100 border-2 border-base-300 focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 btn btn-ghost btn-sm text-base-content/50 hover:text-base-content"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Render dropdown in a portal at document root */}
      {isOpen && createPortal(renderDropdown(), document.body)}
    </>
  );
}; 